import React, { useState } from 'react';
import { ArrowRight, ArrowUpRight, CheckCircle2, Loader2, X } from 'lucide-react';
import { api } from '@/utils/api';
import T from '@/components/landing/copy';
import { tt } from '@/components/landing/atoms';

// Demo modal
// ─────────────────────────────────────────────────────────────────────────────
function DemoRequestModal({ open, onClose, lang }) {
  const [form, setForm] = useState({ name: '', email: '', company: '', phone: '', portfolio_size: '10-50', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  if (!open) return null;
  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true); setError('');
    try {
      await api.submitDemoRequest({
        ...form,
        request_type: 'demo',
        source_page: typeof window !== 'undefined' ? window.location.pathname + window.location.hash : 'landing',
      });
      setSuccess(true);
    }
    catch (err) { setError(err?.response?.data?.detail || 'Could not submit. Please email hello@reva.sa.'); }
    finally { setSubmitting(false); }
  };
  const isAr = lang === 'ar';

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid="reva-demo-modal" dir={isAr ? 'rtl' : 'ltr'}>
      <div className="reva-glass-2 max-w-md w-full p-7 relative">
        <button onClick={onClose} className={`absolute top-4 ${isAr ? 'left-4' : 'right-4'} text-white/65 hover:text-white`} data-testid="reva-demo-close" aria-label="Close"><X className="h-4 w-4" /></button>
        {success ? (
          <div className="text-center py-8">
            <CheckCircle2 className="h-10 w-10 text-[color:var(--reva-mint)] mx-auto" />
            <h3 className="mt-5 reva-display text-2xl">{tt(T.demo.success, lang)}</h3>
            <p className="mt-3 text-[14px] text-white/75">{tt(T.demo.successSub, lang)}</p>
            <button onClick={onClose} className="reva-btn-ghost mt-7">{tt(T.demo.close, lang)}</button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <div>
              <Eyebrow>{tt(T.demo.eyebrow, lang)}</Eyebrow>
              <h3 className="mt-3 reva-display text-2xl">{tt(T.demo.title, lang)}</h3>
            </div>
            {[
              { k: 'name',    ph: T.demo.ph.name,    type: 'text',  req: true },
              { k: 'email',   ph: T.demo.ph.email,   type: 'email', req: true },
              { k: 'company', ph: T.demo.ph.company, type: 'text',  req: true },
              { k: 'phone',   ph: T.demo.ph.phone,   type: 'tel' },
            ].map((f) => (
              <input key={f.k} type={f.type} required={f.req} placeholder={tt(f.ph, lang)} value={form[f.k]} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })} className="w-full bg-white/[0.05] border border-white/[0.12] rounded-xl px-4 py-3 text-[14px] text-white placeholder:text-white/45 focus:outline-none focus:border-[color:var(--reva-signal)] transition-colors" data-testid={`reva-demo-${f.k}`} />
            ))}
            <select value={form.portfolio_size} onChange={(e) => setForm({ ...form, portfolio_size: e.target.value })} className="w-full bg-white/[0.05] border border-white/[0.12] rounded-xl px-4 py-3 text-[14px] text-white focus:outline-none focus:border-[color:var(--reva-signal)] transition-colors" data-testid="reva-demo-portfolio">
              {['1-10', '10-50', '50-200', '200-1000', '1000+'].map((v) => <option key={v} value={v}>{`${v} ${tt(T.demo.ph.units, lang)}`}</option>)}
            </select>
            <textarea placeholder={tt(T.demo.ph.message, lang)} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={3} className="w-full bg-white/[0.05] border border-white/[0.12] rounded-xl px-4 py-3 text-[14px] text-white placeholder:text-white/45 focus:outline-none focus:border-[color:var(--reva-signal)] transition-colors" data-testid="reva-demo-message" />
            {error && <div className="text-[12px] text-red-400">{error}</div>}
            <button type="submit" disabled={submitting} className="reva-btn w-full justify-center" data-testid="reva-demo-submit">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <>{tt(T.demo.submit, lang)} <ArrowRight className="h-4 w-4" /></>}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Quick-tour modal — replaces "See the platform" with a real entry-point chooser
// ─────────────────────────────────────────────────────────────────────────────
function TourModal({ open, onClose, lang, navigate }) {
  if (!open) return null;
  const isAr = lang === 'ar';
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid="reva-tour-modal" dir={isAr ? 'rtl' : 'ltr'}>
      <div className="reva-glass-2 max-w-lg w-full p-7 relative">
        <button onClick={onClose} className={`absolute top-4 ${isAr ? 'left-4' : 'right-4'} text-white/65 hover:text-white`} data-testid="reva-tour-close" aria-label="Close"><X className="h-4 w-4" /></button>
        <Eyebrow>REVA · TOUR</Eyebrow>
        <h3 className="mt-3 reva-display text-2xl">{tt(T.tour.title, lang)}</h3>
        <p className="mt-2 text-[13px] text-white/75">{tt(T.tour.sub, lang)}</p>
        <div className="mt-5 space-y-2">
          {T.tour.items.map((it, i) => (
            <button key={i} onClick={() => { onClose(); navigate(it.to); }} className="w-full text-start rounded-xl border border-white/[0.10] bg-white/[0.04] hover:bg-white/[0.08] hover:border-white/20 transition-colors p-4 flex items-center justify-between gap-3" data-testid={`reva-tour-${i}`}>
              <div>
                <div className="text-[14px] text-white font-medium">{tt(it.t, lang)}</div>
                <div className="text-[12px] text-white/65 mt-0.5">{tt(it.d, lang)}</div>
              </div>
              <ArrowUpRight className="h-4 w-4 text-white/65 shrink-0" />
            </button>
          ))}
        </div>
        <div className="mt-5 pt-5 border-t border-white/[0.08] flex items-center justify-between">
          <span className="text-[12px] text-white/65">{lang === 'ar' ? 'حساب الديمو: ' : 'Demo account: '}<span className="reva-mono text-white">demo@reva.com</span></span>
          <button onClick={() => { onClose(); navigate('/auth'); }} className="reva-btn text-[12px] py-1.5" data-testid="reva-tour-signin">
            {tt(T.tour.primary, lang)} <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}


export { DemoRequestModal, TourModal };
